import React, { useState, useRef, useEffect, useContext } from 'react';
import apiClient from '../../utils/apiClient';
import { useNavigate } from 'react-router-dom';
import TokenService from '../../utils/tokenService';
import { jwtDecode } from 'jwt-decode';
import settingsIcon from '../../assets/icons/settings.svg';
import profileDrop from '../../assets/icons/profileDrop.svg';
import tenDrop from '../../assets/icons/tenantsDrop.svg';
import logDrop from '../../assets/icons/logoutDrop.svg';
import firmwareDrop from '../../assets/icons/firmwareDrop.svg';
import helpDrop from '../../assets/icons/help-circle.svg';
import FirmwareUpdateModal from '../UI/FirmwareUpdateModal';
import DeviceSettingsTemplateModal from '../UI/DeviceSettingsTemplateModal';
import ConfirmationModal from '../UI/ConfirmationModal';
import SwitchTenantModal from '../UI/SwitchTenantModal';
import BrandingService from '../../utils/brandingService';
import '../../styles/Settings.css';

const Dropdown = ({ items, onClose }) => (
  <div className="dropdown">
    {items.map((item, idx) =>
      item.label === '--------' ? (
        <hr key={idx} />
      ) : (
        <div
          key={idx}
          className={`dropdown-item ${item.className || ''}`}
          onClick={() => {
            item.onClick?.();
            onClose();
          }}
        >
          {item.icon && (
            <img src={item.icon} alt={`${item.label} Icon`} className="dropdown-icon" />
          )}
          {item.label}
        </div>
      )
    )}
  </div>
);

const SettingsWidget = () => {
  const [showSettings, setShowSettings] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showFirmwareModal, setShowFirmwareModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showTenantSwitch, setShowTenantSwitch] = useState(false);

  const navigate = useNavigate();
  const settingsRef = useRef(null);

  let isSuperAdmin = false;
  const idToken = TokenService.getIdToken();
  if (idToken) {
    try {
      const { 'custom:roles': roles = '' } = jwtDecode(idToken);
      isSuperAdmin = roles.split(',').map(r => r.trim()).includes('superadmin');
    } catch {
      console.error('Could not decode roles');
    }
  }

  const handleLogout = async () => {
    try {
      const refreshToken = TokenService.getRefreshToken();
      if (refreshToken) {
        await apiClient.post("/signout", { RefreshToken: refreshToken });
      }
      TokenService.clearTokens();
      BrandingService.clearAll();
      navigate('/');
    } catch {
      TokenService.clearTokens();
      BrandingService.clearAll();
      navigate('/');
    }
  };

  const dropdownItems = [
    { label: 'Help Center', icon: helpDrop, onClick: () => navigate('/helpcenter') },
    isSuperAdmin && { label: 'Tenants', icon: tenDrop, onClick: () => navigate('/tenants') },
    isSuperAdmin && {
      label: 'Switch Tenant',
      icon: tenDrop,
      onClick: () => setShowTenantSwitch(true)
    },
    isSuperAdmin && {
      label: 'Device Settings Template',
      icon: settingsIcon,
      onClick: () => setShowTemplateModal(true)
    },
    isSuperAdmin && {
      label: 'Push Firmware Update',
      icon: firmwareDrop,
      onClick: () => setShowFirmwareModal(true)
    },
    { label: 'Profile', icon: profileDrop, onClick: () => navigate('/profile') },
    { label: '--------' },
    {
      label: 'Logout',
      icon: logDrop,
      className: 'logout',
      onClick: () => setShowLogoutConfirm(true)
    }
  ].filter(Boolean);

  const handleSettingsClick = () => setShowSettings(prev => !prev);

  useEffect(() => {
    const handleClickOutside = e => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setShowSettings(false);
      }
    };
    if (showSettings) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettings]);

  return (
    <div style={{ position: 'relative' }} ref={settingsRef}>
      <button className="header-icon-button" onClick={handleSettingsClick}>
        <img src={settingsIcon} alt="Settings" className="settings-icon" />
      </button>

      {showSettings && (
        <div className="settings-panel">
          <Dropdown items={dropdownItems} onClose={() => setShowSettings(false)} />
        </div>
      )}

      {showFirmwareModal && <FirmwareUpdateModal onClose={() => setShowFirmwareModal(false)} />}
      {showTemplateModal && (
        <DeviceSettingsTemplateModal
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
        />
      )}

      <ConfirmationModal
        isOpen={showLogoutConfirm}
        message="Are you sure you want to logout?"
        confirmLabel="Logout"
        cancelLabel="Cancel"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          handleLogout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />

      {showTenantSwitch && (
        <SwitchTenantModal isOpen={showTenantSwitch} onClose={() => setShowTenantSwitch(false)} />
      )}
    </div>
  );
};

export default SettingsWidget;
